import { pixelToDomainX } from "../chartXDomainMapping";
import { Extents } from "../PlotRenderer";

interface Dimensions {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Maps the selected region's dimensions to an absolute time range, or to
 * nothing on an aligned panel.
 *
 * An aligned panel plots each series as seconds elapsed from its own basis,
 * so a region covers a different absolute time range in each series.
 *
 * @param region Dimensions are in pixels relative to the chart
 * @param chartExtents The chart's boundaries
 * @param isAligned Whether the panel's x axis is aligned
 */
export function pixelRegionToAbsoluteTimeRange(
  region: Dimensions,
  chartExtents: Extents,
  isAligned: boolean,
): { startTime: bigint; endTime: bigint } | undefined {
  if (isAligned) {
    return undefined;
  }

  const { viewportLogTime: logTime } = chartExtents.data;

  const startFraction = region.left / chartExtents.pixel.width;
  const endFraction = (region.left + region.width) / chartExtents.pixel.width;
  const duration = Number(logTime.max - logTime.min);

  return {
    startTime: logTime.min + BigInt(Math.round(duration * startFraction)),
    endTime: logTime.min + BigInt(Math.round(duration * endFraction)),
  };
}

/**
 * Maps the selected region's dimensions to a chart's current viewport domain.
 *
 * Returns the current viewport domain unchanged when no domain value lies
 * under the region's edges, which happens when the viewport or the chart
 * area has no width.
 *
 * @param region Dimensions are in pixels relative to the chart
 * @param chartExtents The chart's boundaries
 */
export function pixelRegionToViewport(
  region: Dimensions,
  chartExtents: Extents,
) {
  const { viewportDomain } = chartExtents.data;

  // The region's pixel coordinates are already relative to the chart area's
  // left edge, so the pixel origin here is 0.
  const geometry = {
    viewportDomain,
    pixel: { left: 0, width: chartExtents.pixel.width },
  };

  return {
    min: pixelToDomainX(region.left, geometry) ?? viewportDomain.min,
    max:
      pixelToDomainX(region.left + region.width, geometry) ??
      viewportDomain.max,
  };
}
